import { supabase } from "@/lib/supabase/client";
import { sendWelcomeEmail } from "@/services/emailService";
import { clearWahooToken } from "@/lib/integrations/wahooAuth";
import type { Account, Subscription } from "@/types";
import type { User, Session } from "@supabase/supabase-js";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Map a Supabase User object → our app's Account type.
 * This is the single place where we translate between the two shapes.
 */
function userToAccount(user: User): Account {
  return {
    id: user.id,
    email: user.email ?? "",
    createdAt: user.created_at,
  };
}

/**
 * Fetch the subscription for a user from the Supabase `subscriptions` table.
 * Returns status "none" if no row exists (new user, not yet subscribed).
 */
async function fetchSubscription(userId: string): Promise<Subscription> {
  const { data } = await supabase
    .from("subscriptions")
    .select("*")
    .eq("account_id", userId)
    .maybeSingle();

  if (!data) {
    return {
      accountId: userId,
      status: "none",
      currentPeriodEnd: "",
      provider: "stripe",
    };
  }

  return {
    accountId: userId,
    status: data.status as Subscription["status"],
    currentPeriodEnd: data.current_period_end ?? "",
    provider: "stripe",
    providerCustomerId: data.stripe_customer_id ?? undefined,
    providerSubscriptionId: data.stripe_subscription_id ?? undefined,
  };
}

// ─── Auth Service ─────────────────────────────────────────────────────────────

/**
 * Thin wrapper around supabase.auth that returns our own Account type.
 *
 * All auth state changes are propagated through the onAuthStateChange listener
 * registered in authStore.initializeAuth().  No component should call Supabase
 * directly — always go through this service or useAuthActions().
 */
export const authService = {
  // ── Read ────────────────────────────────────────────────────────────────────

  /**
   * Returns the current session from Supabase's own localStorage cache.
   * This is synchronous from the cache — the async call validates against the
   * server and refreshes the token if needed.
   */
  async getSession(): Promise<{
    account: Account;
    subscription: Subscription;
    session: Session;
  } | null> {
    const { data, error } = await supabase.auth.getSession();

    if (error || !data.session) return null;

    const account = userToAccount(data.session.user);
    return {
      account,
      subscription: await fetchSubscription(account.id),
      session: data.session,
    };
  },

  // ── Write ───────────────────────────────────────────────────────────────────

  async signIn(
    email: string,
    password: string,
  ): Promise<{ account: Account; subscription: Subscription }> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw new Error(error.message);
    if (!data.user) throw new Error("Sign in succeeded but no user returned.");

    const account = userToAccount(data.user);
    return { account, subscription: await fetchSubscription(account.id) };
  },

  async signUp(
    email: string,
    password: string,
    captchaToken?: string,
  ): Promise<{
    account: Account;
    subscription: Subscription;
    requiresEmailConfirmation: boolean;
  }> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });

    // If Supabase fails to send the confirmation email (SMTP misconfigured),
    // the user was still created in auth.users. Fall back to signing in
    // directly — this works if the auto_confirm_user trigger is installed,
    // or if email confirmation is disabled in the dashboard.
    if (error) {
      const isEmailSendError =
        error.message
          .toLowerCase()
          .includes("error sending confirmation email") ||
        error.message.toLowerCase().includes("email sending") ||
        error.message.toLowerCase().includes("smtp");

      if (isEmailSendError) {
        const { data: signInData, error: signInError } =
          await supabase.auth.signInWithPassword({ email, password });

        if (!signInError && signInData.user && signInData.session) {
          const account = userToAccount(signInData.user);
          // Fire welcome email — non-blocking, ignore failures
          void sendWelcomeEmail(email).catch(() => {});
          return {
            account,
            subscription: await fetchSubscription(account.id),
            requiresEmailConfirmation: false,
          };
        }
      }

      throw new Error(error.message);
    }

    // After signUp, Supabase may return a user with an unconfirmed email.
    // The handle_new_user() trigger will create the profile row automatically.
    if (!data.user) throw new Error("Sign up succeeded but no user returned.");

    // When email confirmation is enabled in Supabase, data.session is null —
    // the user must click the confirmation link before they have a live session.
    const requiresEmailConfirmation = !data.session;

    const account = userToAccount(data.user);

    // Fire welcome email — non-blocking, ignore failures
    void sendWelcomeEmail(email).catch(() => {});

    return {
      account,
      subscription: await fetchSubscription(account.id),
      requiresEmailConfirmation,
    };
  },

  async signInWithGoogle(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) throw new Error(error.message);
  },

  async signOut(): Promise<void> {
    clearWahooToken(); // remove token while _userId is still set
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  },

  /**
   * Send a password-reset email. The email contains a link that redirects
   * back to /reset-password where the user sets their new password.
   *
   * Always returns { success: true } even if the email isn't registered so
   * we don't leak which addresses exist in the database.
   */
  async requestPasswordReset(
    email: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      // Swallow "user not found" — show success to prevent enumeration
      if (error && !error.message.toLowerCase().includes("user not found")) {
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch {
      return {
        success: false,
        error: "Something went wrong. Please try again.",
      };
    }
  },

  /**
   * Update the current user's password. Call this from /reset-password after
   * Supabase has exchanged the recovery token for a session.
   */
  async updatePassword(
    newPassword: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch {
      return {
        success: false,
        error: "Failed to update password. Please try again.",
      };
    }
  },

  // ── Listener ─────────────────────────────────────────────────────────────────

  /**
   * Subscribe to auth state changes (sign-in, sign-out, token refresh, etc).
   * Returns a cleanup function — call it when the subscriber unmounts.
   *
   * This is the primary mechanism by which the authStore stays in sync with
   * Supabase's session state.
   */
  onAuthStateChange(
    callback: (
      payload: {
        account: Account;
        subscription: Subscription;
      } | null,
    ) => void,
  ): () => void {
    // Each sign-in starts a new "generation". If sign-out fires while a
    // subscription fetch is in-flight, we bump the generation so the stale
    // fetch result is discarded and can't re-authenticate the user.
    let generation = 0;
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // PASSWORD_RECOVERY means the user clicked a reset link — they are NOT
      // fully authenticated yet. Let ResetPasswordPage handle this event on its
      // own listener. If we set isAuthenticated here the user bypasses login.
      if (event === "PASSWORD_RECOVERY") return;

      if (session?.user) {
        const account = userToAccount(session.user);
        const thisGen = ++generation;
        void fetchSubscription(account.id)
          .then((subscription) => {
            if (thisGen !== generation) return; // stale — a newer event (e.g. sign-out) fired
            callback({ account, subscription });
          })
          .catch((err) => {
            console.error("[authService] fetchSubscription failed:", err);
            if (thisGen !== generation) return;
            // Treat subscription as null so the user reaches the billing page
            // rather than being stuck on the loading screen.
            callback({ account, subscription: null as never });
          });
      } else {
        ++generation; // invalidate any in-flight subscription fetch
        callback(null);
      }
    });

    // Return the unsubscribe function so the caller can clean up
    return () => data.subscription.unsubscribe();
  },
};
